import { useQuery } from '@apollo/client';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { CreateProduct } from '@thro-types/leihbar';
import { User } from '@thro-types/users';

import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import { GQLGuard } from 'app/components';
import { UsersQueryArgs } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

export const TargetUserAutocomplete = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const { setFieldValue, values } = useFormikContext<CreateProduct & { targetUsers: User[] }>();

  const getUsersQuery = useQuery<{ users: User[] }, UsersQueryArgs>(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      type: UserType.SHIBBOLETH,
      limit: 0
    }
  });

  useEffect(() => {
    setLoading(getUsersQuery.loading);
    if (!!getUsersQuery.data) {
      setOptions([...getUsersQuery.data?.users]);

      if (values.targetUserIds && !values.targetUsers) {
        const targetUsers = values.targetUserIds.map(id =>
          getUsersQuery.data?.users.find(user => user.id === id)
        );
        setFieldValue('targetUsers', targetUsers);
      }
    }
  }, [getUsersQuery]);

  const changeHandler = value => {
    let filter: string | undefined = undefined;
    if (value !== '') {
      filter = JSON.stringify({ email: { term: value } });
    }
    getUsersQuery.refetch({ ids: undefined, filter: filter, type: UserType.SHIBBOLETH });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceChangeHandler = useCallback(
    debounce(value => {
      changeHandler(value);
    }, 500),
    []
  );

  const handleChange = (_: unknown, value: User[]) => {
    if (!value) return;
    const targetUsers = value;
    const targetUserIds = targetUsers.map(item => item.id);
    setFieldValue('targetUsers', targetUsers);
    setFieldValue('targetUserIds', targetUserIds);
  };

  return (
    <GQLGuard query={getUsersQuery}>
      {data => {
        return (
          <Autocomplete
            fullWidth
            multiple
            value={values.targetUsers || []}
            open={open}
            size="small"
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onInputChange={(event, value) => {
              debounceChangeHandler(value);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={option => option.email}
            onChange={handleChange}
            options={options}
            loading={loading}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={t(translations.pages.events.create.organizingPersonsPlaceholder())}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />
            )}
          />
        );
      }}
    </GQLGuard>
  );
};
